import cn from "classnames";
import React from "react";
import { useIntl } from "react-intl";
import "./CampaignsColumnsPopover.scss";

export type CampaignColumnKey =
  | "platform"
  | "format"
  | "campaign"
  | "adsetGroup"
  | "spend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "cpm"
  | "cpc"
  | "ctr"
  | "conversionRate";

const LEFT_KEYS: CampaignColumnKey[] = [
  "platform",
  "format",
  "campaign",
  "adsetGroup",
  "spend",
  "impressions",
];

const RIGHT_KEYS: CampaignColumnKey[] = [
  "clicks",
  "conversions",
  "cpm",
  "cpc",
  "ctr",
  "conversionRate",
];

export const CAMPAIGN_TABLE_COLUMN_KEYS: CampaignColumnKey[] = [
  ...LEFT_KEYS,
  ...RIGHT_KEYS,
];

/** Orden y anchos de la tabla principal de campañas (alineado al selector de columnas). */
export const CAMPAIGN_COLUMN_CONFIG: { key: CampaignColumnKey; width: number }[] = [
  { key: "platform", width: 120 },
  { key: "format", width: 130 },
  { key: "campaign", width: 280 },
  { key: "adsetGroup", width: 170 },
  { key: "spend", width: 130 },
  { key: "impressions", width: 130 },
  { key: "clicks", width: 110 },
  { key: "conversions", width: 120 },
  { key: "cpm", width: 110 },
  { key: "cpc", width: 110 },
  { key: "ctr", width: 100 },
  { key: "conversionRate", width: 130 },
];

export function campaignColumnIndex(key: CampaignColumnKey): number {
  return CAMPAIGN_TABLE_COLUMN_KEYS.indexOf(key);
}

/** Índice de la columna Campaña (no se puede ocultar). */
export const CAMPAIGN_COLUMN_LOCKED_INDEX = campaignColumnIndex("campaign");

type Props = {
  visibility: boolean[];
  onToggle: (idx: number) => void;
};

export default function CampaignsColumnsPopover({ visibility, onToggle }: Props) {
  const intl = useIntl();

  const label = (key: CampaignColumnKey) =>
    intl.formatMessage({ id: `app.advertisements.columns.${key}` });

  const renderRow = (key: CampaignColumnKey) => {
    const idx = campaignColumnIndex(key);
    const locked = key === "campaign";
    const id = `fb-campaigns-col-${key}`;

    return (
      <div
        key={key}
        className={cn(
          "fb-campaigns-columns-popover__row",
          locked && "fb-campaigns-columns-popover__row--locked"
        )}
      >
        <span className="fb-campaigns-columns-popover__label" id={id}>
          {label(key)}
        </span>
        <label className="fb-campaigns-columns-popover__switch">
          <input
            type="checkbox"
            role="switch"
            className="fb-campaigns-columns-popover__input"
            checked={visibility[idx]}
            disabled={locked}
            onChange={() => onToggle(idx)}
            aria-labelledby={id}
          />
          <span className="fb-campaigns-columns-popover__track" aria-hidden />
        </label>
      </div>
    );
  };

  return (
    <div
      className="fb-campaigns-columns-popover"
      role="dialog"
      aria-label={intl.formatMessage({
        id: "app.advertisements.toolbar.columns",
      })}
    >
      <div className="fb-campaigns-columns-popover__grid">
        <div className="fb-campaigns-columns-popover__col">
          {LEFT_KEYS.map(renderRow)}
        </div>
        <div className="fb-campaigns-columns-popover__divider" aria-hidden />
        <div className="fb-campaigns-columns-popover__col">
          {RIGHT_KEYS.map(renderRow)}
        </div>
      </div>
    </div>
  );
}
